import { JwtPayload } from 'jsonwebtoken';

/**
 * Enum representing the remote state in Okta.
 *
 * Explicitly numbered to prevent the number from changing between deployments.
 * i.e. the browser is running an older version than the backend Next.js server
 */
export enum OktaSessionState {
  /**
   * We looked at Okta, and they are not a valid user or the session is not present. The user should be logged out.
   */
  UNAUTHENTICATED = 0,
  /**
   * We have a session token, but we need to exchange it ASAP for a real session
   */
  INITIAL_SESSION = 1,
  /**
   * We have a session in Okta and it's valid
   */
  SESSION_VALID = 2,
  /**
   * The session expired, we should try to refresh it, or mark the user as unauthenticated
   */
  SESSION_EXPIRED = 3,
}

/**
 * * @example ```
 * * {
 *  id: "00u48xjpkfICfsDQO4x7",
 *  email: "robert.smieja@quattr.com",
 *  expires_at: "2022-09-27T03:06:21.000Z",
 *  name: "Robert Smieja",
 *  session_token: "20111HGJDIlnFbNZCOTEO4wlL12RL9D511ENNgtzm8PGODFJeMVkSiY",
 *  default_schema: "_POCK",
 *  groups: [
 *    "_POCK",
 *    "_SUBS",
 *    "_MAGI",
 *    "_HSNG",
 *    "_PADS",
 *    "_247A",
 *    "_CRSR",
 *    "_SHLD",
 *    "_TELE",
 *    "_QTTR",
 *    "_IACI",
 *    "_AMEX",
 *    "_MCAF",
 *  ],
 *}
 * ```
 */
export interface OktaCredentialsUser {
  /**
   * set to the user ID from Okta
   */
  id: string;
  name: string;
  email: string;

  /**
   * ISO Timestamp for when the session/token expires
   */
  expires_at?: string;

  /**
   * Temporary session token, only present after initial sign-in from Okta.
   * Expires in 5 min, needs to be exchanged for an actual session.
   *
   * @see https://developer.okta.com/docs/guides/session-cookie/main/#retrieve-a-session-cookie-through-the-openid-connect-authorization-endpoint
   */
  session_token?: string;

  /**
   * ID token for the session, set after trading in the temporary session token
   */
  id_token?: string;

  /**
   * Typically a list of schemas, i.e. ['_QTTR']
   *
   * May be undefined if not returned from Okta, i.e. 'groups' was left out of authorization scope
   */
  groups?: string[];
  /**
   * Typically a string, i.e. '_QTTR'
   *
   * May be undefined if not returned from Okta
   */
  default_schema?: string;
  /**
   * Actually a number. Varies between user and dev/prod looker
   * DEPRECATED - not it comes from Auth0 -> app_metadata -> selected_schema
   * May be undefined if not returned from Okta, i.e. a new user
   */
  looker_id?: string;

  userType?: string;
  /** Domains a trial user has access to */
  subscription_status?: string;
}

/**
 * Extensions/customizations to default Next-Auth Profile
//  */
// export interface QwaProfile {
//   groups: string[];
//   default_schema?: string;
//   looker_id?: number;
// }

/**
 * Extensions/customizations to the default Next-Auth JWT
 */
export interface QwaSession {
  // domain?: string;

  okta_state?: OktaSessionState;

  /**
   * CSRF token to use for Okta, as the "state" for the GET request, when retrieving auth details on the client side.
   * For security, should be returned to us in the resulting token
   * TODO: Should this be a JWT or something else reversible?
   */
  okta_csrf_token: string;

  /**
   * Nonce to use for Okta. Prevents replay attacks, should be set to a unique value each time the JWT is created
   */
  okta_nonce: string;

  /**
   * In milliseconds since epoch
   */
  okta_expires_at?: number;

  /**
   * ID token for the session, set after trading in the temporary session token
   */
  id_token?: string;

  /**
   * Refresh token to use when refreshing the user's session on their behalf
   */
  refresh_token?: string;

  /**
   * TODO Is this even used?
   */
  // access_token?: string;

  /**
   * Looker auth token, from Looker API call
   */
  user_auth_token?: string;

  /**
   * Looker auth token expiration in milliseconds
   * TODO is there a way to have this be longer than 1 hour?
   */
  user_auth_token_expiration?: number;

  /**
   * Temporary session token, only present after initial sign-in from Okta.
   * Expires in 5 min, needs to be exchanged for an actual session.
   *
   * @see https://developer.okta.com/docs/guides/session-cookie/main/#retrieve-a-session-cookie-through-the-openid-connect-authorization-endpoint
   */
  session_token?: string;

  // OKTA Redirect to finish login. Will be unset if unneeded
  sessionRedirect?: string;

  okta_session_id?: string;

  /**
   * This field should only be present for Test Drive Tokens
   */
  test_drive_domain?: string;
  user: QwaUser;

  /**
   * Currently selected schema in the session
   */
  selected_schema: string;
}

export interface QwaUser {
  // TODO this was removed to save space in the cookie, if needed can be added back in
  okta_user_id?: string;
  name?: string;
  email?: string;
  userType?: string;
  groups: string[];
  looker_id?: number;
  subscription_status?: string;
  qwa_tools_subscription_status?: string;
  planId?: string;
  user_id?: string | null | undefined;
  isContentAiSignUp?: boolean;
  isToolsSwitchEmailSent?: boolean;
}

export const enum AuthenticationStatus {
  Authenticated = 'Authenticated',
  Unauthenticated = 'Unauthenticated',
  Loading = 'Loading',
}

export interface OktaIdTokenJwt extends JwtPayload {
  sub: string;
  name: string;
  email: string;
  ver: number;
  /**
   * URL, i.e. "https://login.quattr.com/oauth2/aus1r3tsuyPZkOpBx4x7"
   */
  iss: string;
  aud: string;
  /**
   * Issued At Time, in milliseconds since epoch
   */
  iat: number;
  /**
   * Expiration, in milliseconds since epoch
   */
  exp: number;
  jti: string;
  amr: string[];
  nonce: string;
  /**
   * Typically an e-mail address?
   */
  preferred_username: string;
  /**
   * Milliseconds since epoch
   */
  auth_time: number;
  at_hash: string;
  default_schema: string;
  grp: string[];

  /**
   * @example 'en_US'
   */
  locale: string;

  idp: string;

  given_name: string;
  family_name: string;
  /**
   * @example 'America/Los_Angeles'
   */
  zoneinfo: string;
  /**
   * In seconds since epoch
   */
  updated_at: number;
  email_verified: boolean;
}
